import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FlatList, RefreshControl, StyleSheet } from 'react-native';

import { useIsFocused } from '@react-navigation/native';

import { BottomFooterRG } from '@/components/BottomFooterRG';
import { LineSeparator } from '@/components/LineSeparator';
import { Screen } from '@/components/ScreenComponent';
import { ScreenNavBar } from '@/components/ScreenNavBar';
import { SizedBox } from '@/components/SizedBox';
import { Box } from '@/components/lib/components/Box';
import { ds } from '@/components/lib/design.system';
import { FeaturedBetCard } from '@/feature/lobby-sbk/components/FeaturedBets/CardEvent/FeaturedBetCard';
import { FeaturedBet } from '@/feature/lobby-sbk/components/FeaturedBets/types';
import { useFeaturedBets } from '@/feature/lobby-sbk/hooks/use-featured-bets';
import { useFetchOnResume } from '@/hooks/use-fetch-resume';
import { useForceRefresh } from '@/hooks/use-force-refresh';
import { LoadingScreen } from '@/screens/LoadingScreen';
import { common, designSystem } from '@/styles/styles';

const ItemSeparator = () => <SizedBox value={16} />;

export const FeaturedHubScreen = () => {
    const { t } = useTranslation('lobby_sbk');

    const isFocused = useIsFocused();
    const { data: featuredBets, refetch, isLoading } = useFeaturedBets({ limit: 20 }, isFocused);
    const { forceRefreshing, onRefresh } = useForceRefresh(refetch);
    useFetchOnResume(refetch);

    const renderCardBodyCallback = useCallback(({ item }: { item: FeaturedBet }) => {
        return <FeaturedBetCard key={item.id} featuredBet={item} />;
    }, []);

    if (isLoading) {
        return <LoadingScreen />;
    }

    return (
        <Screen>
            <ScreenNavBar title={t('featured_picks')} />
            <LineSeparator style={common.hairlineHeight} />
            <Box paddingHorizontal="s16">
                <FlatList
                    contentContainerStyle={styles.list}
                    keyExtractor={featuredBet => `${featuredBet.id}`}
                    data={featuredBets}
                    renderItem={renderCardBodyCallback}
                    ItemSeparatorComponent={ItemSeparator}
                    refreshControl={
                        <RefreshControl
                            enabled
                            onRefresh={onRefresh}
                            refreshing={forceRefreshing}
                            colors={[designSystem.colors.neutral700]}
                            tintColor={ds.colorValue.icon.primary}
                        />
                    }
                    ListFooterComponent={<BottomFooterRG footerMarginTop="s64" />}
                />
            </Box>
        </Screen>
    );
};

const styles = StyleSheet.create({
    list: {
        paddingBottom: 64,
        paddingTop: 16,
    },
});
